import React, { useState, useEffect } from 'react';
import { UserX, UserCheck, AlertCircle, Search, RefreshCw } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const ResignBlock = () => {
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState('all'); // all, active, blocked

  useEffect(() => {
    fetchStaff();
  }, []);

  const fetchStaff = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/staff/all-with-blocked`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStaff(response.data || []);
    } catch (error) {
      console.error('Error fetching staff:', error);
      // Fallback to regular staff endpoint
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API_URL}/api/staff/`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setStaff(response.data || []);
      } catch (err) {
        console.error('Error:', err);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleBlock = async (staffId, staffName) => {
    if (!window.confirm(`Are you sure you want to BLOCK "${staffName}"? They will not be able to access the system.`)) {
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_URL}/api/staff/${staffId}/block`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert(`${staffName} has been blocked successfully.`);
      fetchStaff();
    } catch (error) {
      console.error('Error blocking staff:', error);
      alert('Failed to block staff. Please try again.');
    }
  };

  const handleUnblock = async (staffId, staffName) => {
    if (!window.confirm(`Are you sure you want to UNBLOCK "${staffName}"?`)) {
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_URL}/api/staff/${staffId}/unblock`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert(`${staffName} has been unblocked successfully.`);
      fetchStaff();
    } catch (error) {
      console.error('Error unblocking staff:', error);
      alert('Failed to unblock staff. Please try again.');
    }
  };

  // Filter staff based on search and filter
  const filteredStaff = staff.filter(member => {
    const matchesSearch = member.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         member.employee_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         member.email?.toLowerCase().includes(searchTerm.toLowerCase());
    
    if (filter === 'active') return matchesSearch && !member.is_blocked;
    if (filter === 'blocked') return matchesSearch && member.is_blocked;
    return matchesSearch;
  });

  const activeCount = staff.filter(s => !s.is_blocked).length;
  const blockedCount = staff.filter(s => s.is_blocked).length;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Resign / Block Staff</h1>
          <p className="text-sm text-gray-500 mt-1">Manage staff resignation and blocking</p>
        </div>
        <button 
          onClick={fetchStaff}
          className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <UserCheck className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Total Staff</p>
              <p className="text-xl font-bold text-gray-900">{staff.length}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <UserCheck className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Active Staff</p>
              <p className="text-xl font-bold text-green-600">{activeCount}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-100 rounded-lg">
              <UserX className="h-5 w-5 text-red-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Blocked Staff</p>
              <p className="text-xl font-bold text-red-600">{blockedCount}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search by name, employee ID, or email..."
              className="w-full pl-10 pr-4 py-2 border rounded-lg"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setFilter('all')}
              className={`px-4 py-2 rounded-lg ${filter === 'all' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700'}`}
            >
              All ({staff.length})
            </button>
            <button
              onClick={() => setFilter('active')}
              className={`px-4 py-2 rounded-lg ${filter === 'active' ? 'bg-green-600 text-white' : 'bg-gray-100 text-gray-700'}`}
            >
              Active ({activeCount})
            </button>
            <button
              onClick={() => setFilter('blocked')}
              className={`px-4 py-2 rounded-lg ${filter === 'blocked' ? 'bg-red-600 text-white' : 'bg-gray-100 text-gray-700'}`}
            >
              Blocked ({blockedCount})
            </button>
          </div>
        </div>
      </div>

      {/* Staff Table */}
      <div className="bg-white rounded-lg shadow overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Employee ID</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Department</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Designation</th>
              <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Status</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Action</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {loading ? (
              <tr>
                <td colSpan="6" className="px-6 py-8 text-center">
                  <RefreshCw className="h-6 w-6 animate-spin mx-auto text-gray-400" />
                  <p className="mt-2 text-gray-500">Loading staff...</p>
                </td>
              </tr>
            ) : filteredStaff.length === 0 ? (
              <tr>
                <td colSpan="6" className="px-6 py-8 text-center text-gray-500">
                  <AlertCircle className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                  No staff members found
                </td>
              </tr>
            ) : (
              filteredStaff.map((member) => (
                <tr key={member.id} className={member.is_blocked ? 'bg-red-50' : ''}>
                  <td className="px-6 py-4 text-sm text-gray-500">{member.employee_id}</td>
                  <td className="px-6 py-4">
                    <div>
                      <p className="text-sm font-medium text-gray-900">{member.full_name}</p>
                      <p className="text-xs text-gray-500">{member.email}</p>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">{member.department || '-'}</td>
                  <td className="px-6 py-4 text-sm text-gray-500">{member.designation || '-'}</td>
                  <td className="px-6 py-4 text-center">
                    <span className={`px-3 py-1 text-xs font-semibold rounded-full ${
                      member.is_blocked 
                        ? 'bg-red-100 text-red-800' 
                        : 'bg-green-100 text-green-800'
                    }`}>
                      {member.is_blocked ? 'Blocked' : 'Active'}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-right">
                    {member.is_blocked ? (
                      <button 
                        onClick={() => handleUnblock(member.id, member.full_name)}
                        className="inline-flex items-center gap-1 px-3 py-1 bg-green-100 text-green-700 text-sm font-medium rounded-lg hover:bg-green-200"
                      >
                        <UserCheck className="h-4 w-4" />
                        Unblock
                      </button>
                    ) : (
                      <button 
                        onClick={() => handleBlock(member.id, member.full_name)}
                        className="inline-flex items-center gap-1 px-3 py-1 bg-red-100 text-red-700 text-sm font-medium rounded-lg hover:bg-red-200"
                      >
                        <UserX className="h-4 w-4" />
                        Block
                      </button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Info Note */}
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <div className="flex gap-3">
          <AlertCircle className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="text-sm font-medium text-yellow-800">Important Note</h4>
            <p className="text-sm text-yellow-700 mt-1">
              Blocking a staff member will prevent them from accessing the system. 
              They will not appear in attendance, payroll, or other active staff lists.
              You can unblock them at any time to restore their access.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResignBlock;
